export interface FilterSummaryOptions {
    [option: string]: number;
}

export enum FilterSummaryGroups {
    INVENTORY_TYPES = 'inventoryTypes',
    YEARS = 'years',
    MAKES = 'makes',
    MODELS = 'models',
    TRIMS = 'trims',
    EXTCOLORS = 'extColors',
    INTCOLORS = 'intColors',
    ENGINES = 'engines',
    BODY_STYLES = 'bodyStyles'
}

export enum CustomFilterGroups {
    PRICE = 'price',
    MILEAGE = 'mileage',
    AGE = 'age',
    SEARCH = 'search',
    DEALERSHIPS = 'dealerships'
}

export enum FilterGroupTypes {
    INPUT = 'input',
    CHECKBOX = 'checkbox'
}

export type FilterSummary = {
    [filterGroup in FilterSummaryGroups]: FilterSummaryOptions;
} & {
    count: number;
};

export type FilterItemCheckboxValue = boolean;
export type FilterItemInputValue = string;
export type FilterItemValue = FilterItemCheckboxValue | FilterItemInputValue;

export type FilterItem<T = FilterItemValue> = {
    label: string;
    value: T;
    queryParameter: VehicleSearchQueryParam;
    normalizedLabel?: string; // Optional normalized label for case-insensitive comparison
};

export interface FilterGroupSettings {
    reverse?: boolean;
}

export interface FilterGroupBase {
    id: CustomFilterGroups | FilterSummaryGroups;
    title: string;
    type: FilterGroupTypes;
    settings?: FilterGroupSettings;
}

export type CheckboxFilterGroup = FilterGroupBase & {
    type: FilterGroupTypes.CHECKBOX;
    items: FilterItem<FilterItemCheckboxValue>[];
};

export type InputFilterGroup = FilterGroupBase & {
    type: FilterGroupTypes.INPUT;
    items: [FilterItem<FilterItemInputValue>, FilterItem<FilterItemInputValue>];
    prefix?: string;
    suffix?: string;
};

export type FilterGroup = CheckboxFilterGroup | InputFilterGroup;

export const isCheckboxFilter = (filter: FilterGroup): filter is CheckboxFilterGroup => {
    return filter.type === FilterGroupTypes.CHECKBOX;
};

export const isInputFilter = (filter: FilterGroup): filter is InputFilterGroup => {
    return filter.type === FilterGroupTypes.INPUT;
};

export type AppliedInputFilter = Omit<InputFilterGroup, 'items'> & InputFilterGroup['items'][number];
export type AppliedCheckboxFilter = Omit<CheckboxFilterGroup, 'items'> & CheckboxFilterGroup['items'][number];
export type AppliedFilter = AppliedInputFilter | AppliedCheckboxFilter;

export interface AppliedFilterTag {
    tagLabel: string;
    appliedFilters: AppliedFilter[];
    groupId: string;
}

export type ExpandedFiltersState = {
    [key: string]: boolean;
};

export type ExpandedFiltersAccordionState = {
    [key: string]: boolean;
};

export type VehicleSearchV2QueryParams = {
    commonOrgDealerId: string;
    // Pagination Query parameters
    page?: string;
    pageSize?: string;
    // Sorting Query Parameter
    sort?: string;
} & VehicleSearchV2FilterQueryParams;

export interface VehicleSearchV2FilterQueryParams {
    vehicleIdentifier?: string;
    search?: string;
    vin?: string;
    stockNumber?: string;
    inventoryType?: string;
    year?: string;
    make?: string;
    model?: string;
    trim?: string;
    bodyStyle?: string;
    minPrice?: string;
    maxPrice?: string;
    minOdometer?: string;
    maxOdometer?: string;
    minAgeDays?: string;
    maxAgeDays?: string;
    intColor?: string;
    extColor?: string;
    engine?: string;
}

export type VehicleSearchQueryParam = keyof VehicleSearchV2QueryParams;

export type Sort =
    | 'yearDesc'
    | 'yearAsc'
    | 'inventoryTypeDesc'
    | 'inventoryTypeAsc'
    | 'ageAsc'
    | 'ageDesc'
    | 'odometerAsc'
    | 'odometerDesc'
    | 'listedPriceAsc'
    | 'listedPriceDesc';

export type SortOption = 'default' | Sort;

export interface VehicleSearchOptions {
    appliedFilters?: AppliedFilter[];
    search?: string;
    sort?: Sort;
    commonOrgDealerId: string;
    page?: number;
    pageSize?: number;
}
